import React, { useState, useEffect } from 'react';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { portalTeamAPI } from '../../services/portalAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface TeamMember {
  id: number;
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  role_display: string;
  permissions: {
    role: string;
    is_portal_active: boolean;
    can_place_orders: boolean;
    can_view_invoices: boolean;
    can_access_bridge_club: boolean;
    can_manage_bridge_club_members: boolean;
    can_redeem_rewards: boolean;
    can_view_store_credits: boolean;
    can_manage_team: boolean;
  };
  created_at: string;
  created_by: string | null;
  last_login: string | null;
  is_current_user: boolean;
}

interface TeamData {
  team_members: TeamMember[];
  can_manage_team: boolean;
  store_name: string;
}

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner/Manager', description: 'Full access to all features' },
  { value: 'staff', label: 'Staff', description: 'Orders, invoices, and Bridge Club' },
  { value: 'cashier', label: 'Cashier', description: 'Bridge Club check-in and redemptions only' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
];

const PortalTeam: React.FC = () => {
  const { user } = usePortalAuth();
  const [teamData, setTeamData] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add member modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', first_name: '', last_name: '', role: 'staff' });
  const [addLoading, setAddLoading] = useState(false);

  // Edit member modal state
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Remove confirmation state
  const [removingMember, setRemovingMember] = useState<TeamMember | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await portalTeamAPI.listMembers();
      setTeamData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.email) return;

    try {
      setAddLoading(true);
      setError('');
      const response = await portalTeamAPI.addMember({
        email: addForm.email,
        first_name: addForm.first_name,
        last_name: addForm.last_name,
        role: addForm.role,
        send_invite: true,
      });
      setSuccess(response.data.message);
      setShowAddModal(false);
      setAddForm({ email: '', first_name: '', last_name: '', role: 'staff' });
      loadTeamData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add team member');
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingMember || !editRole) return;

    try {
      setEditLoading(true);
      setError('');
      const response = await portalTeamAPI.updateMember({
        member_id: editingMember.id,
        role: editRole,
      });
      setSuccess(response.data.message);
      setEditingMember(null);
      loadTeamData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update team member');
    } finally {
      setEditLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!removingMember) return;

    try {
      setRemoveLoading(true);
      setError('');
      const response = await portalTeamAPI.removeMember(removingMember.id);
      setSuccess(response.data.message);
      setRemovingMember(null);
      loadTeamData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove team member');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleResendInvite = async (member: TeamMember) => {
    try {
      setError('');
      const response = await portalTeamAPI.resendInvite(member.id);
      setSuccess(response.data.message);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend invitation');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading team data...</div>
      </div>
    );
  }

  const canManage = teamData?.can_manage_team || false;

  return (
    <div style={styles.container}>
      <TutorialAutoStart pageKey="team" />

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Team Management</h1>
          {teamData && <p style={styles.subtitle}>{teamData.store_name}</p>}
        </div>
        <TutorialHelpButton pageKey="team" />
      </div>


      {error && (
        <div style={styles.errorBanner}>
          {error}
          <button onClick={() => setError('')} style={styles.dismissBtn}>×</button>
        </div>
      )}

      {success && (
        <div style={styles.successBanner}>
          {success}
          <button onClick={() => setSuccess('')} style={styles.dismissBtn}>×</button>
        </div>
      )}

      {!canManage && (
        <div style={styles.infoBanner}>
          You have read-only access to view team members. Contact your store owner to manage team access.
        </div>
      )}

      <div style={styles.content}>
        <div style={styles.toolbar}>
          <h2 style={styles.sectionTitle}>
            Team Members ({teamData?.team_members.length || 0})
          </h2>
          {canManage && (
            <button onClick={() => setShowAddModal(true)} style={styles.addButton} data-tutorial="invite-btn">
              + Add Team Member
            </button>
          )}
        </div>

        <div style={styles.teamList} data-tutorial="team-table">
          {teamData?.team_members.map((member, index) => (
            <div key={member.id} style={styles.memberCard}>
              <div style={styles.memberInfo}>
                <div style={styles.memberName}>
                  {member.full_name}
                  {member.is_current_user && <span style={styles.youBadge}>(You)</span>}
                </div>
                <div style={styles.memberEmail}>{member.email}</div>
                <div style={styles.memberMeta}>
                  <span style={{
                    ...styles.roleBadge,
                    backgroundColor: member.role === 'owner' ? '#4CAF50' :
                                     member.role === 'staff' ? '#2196F3' :
                                     member.role === 'cashier' ? '#FF9800' : '#9E9E9E'
                  }}>
                    {member.role_display}
                  </span>
                  <span style={styles.metaText}>
                    Last login: {formatDate(member.last_login)}
                  </span>
                </div>
              </div>

              {canManage && !member.is_current_user && (
                <div style={styles.memberActions} data-tutorial={index === 0 ? "member-actions" : undefined}>
                  <button
                    onClick={() => {
                      setEditingMember(member);
                      setEditRole(member.role);
                    }}
                    style={styles.actionBtn}
                  >
                    Edit Role
                  </button>
                  {!member.last_login && (
                    <button
                      onClick={() => handleResendInvite(member)}
                      style={styles.actionBtnSecondary}
                    >
                      Resend Invite
                    </button>
                  )}
                  <button
                    onClick={() => setRemovingMember(member)}
                    style={styles.actionBtnDanger}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}

          {(!teamData?.team_members || teamData.team_members.length === 0) && (
            <div style={styles.emptyState}>
              <p>No team members found.</p>
              {canManage && (
                <button onClick={() => setShowAddModal(true)} style={styles.addButton}>
                  Add Your First Team Member
                </button>
              )}
            </div>
          )}
        </div>

        {/* Role Descriptions */}
        <div style={styles.roleInfo}>
          <h3 style={styles.roleInfoTitle}>Role Permissions</h3>
          <div style={styles.roleGrid}>
            {ROLE_OPTIONS.map((role) => (
              <div key={role.value} style={styles.roleCard}>
                <div style={styles.roleCardTitle}>{role.label}</div>
                <div style={styles.roleCardDesc}>{role.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Add Team Member</h3>
              <button onClick={() => setShowAddModal(false)} style={styles.modalClose}>×</button>
            </div>
            <form onSubmit={handleAddMember}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  style={styles.input}
                  placeholder="employee@example.com"
                  required
                />
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>First Name</label>
                  <input
                    type="text"
                    value={addForm.first_name}
                    onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })}
                    style={styles.input}
                    placeholder="John"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Last Name</label>
                  <input
                    type="text"
                    value={addForm.last_name}
                    onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })}
                    style={styles.input}
                    placeholder="Smith"
                  />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select
                  value={addForm.role}
                  onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                  style={styles.select}
                  data-tutorial="role-select"
                >
                  {ROLE_OPTIONS.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label} - {role.description}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.modalFooter}>
                <button type="button" onClick={() => setShowAddModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={addLoading} style={styles.submitBtn}>
                  {addLoading ? 'Sending Invite...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {editingMember && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Edit Role - {editingMember.full_name}</h3>
              <button onClick={() => setEditingMember(null)} style={styles.modalClose}>×</button>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Role</label>
              <select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                style={styles.select}
              >
                {ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label} - {role.description}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setEditingMember(null)} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={editLoading || editRole === editingMember.role}
                style={styles.submitBtn}
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {removingMember && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Remove Team Member</h3>
              <button onClick={() => setRemovingMember(null)} style={styles.modalClose}>×</button>
            </div>
            <p style={styles.confirmText}>
              Are you sure you want to remove <strong>{removingMember.full_name}</strong> ({removingMember.email}) from the team?
            </p>
            <p style={styles.confirmSubtext}>
              They will no longer be able to access the portal.
            </p>
            <div style={styles.modalFooter}>
              <button onClick={() => setRemovingMember(null)} style={styles.cancelBtn}>
                Cancel
              </button>
              <button
                onClick={handleRemoveMember}
                disabled={removeLoading}
                style={styles.dangerBtn}
              >
                {removeLoading ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PortalFooter />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: '4px 0 0 0',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#333',
    margin: 0,
  },
  addButton: {
    backgroundColor: '#8B2635',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  },
  teamList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  memberCard: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  memberInfo: {
    flex: 1,
  },
  memberName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  youBadge: {
    fontSize: '12px',
    color: '#666',
    fontWeight: 'normal',
  },
  memberEmail: {
    fontSize: '14px',
    color: '#666',
    marginTop: '2px',
  },
  memberMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '8px',
  },
  roleBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'white',
  },
  metaText: {
    fontSize: '12px',
    color: '#999',
  },
  memberActions: {
    display: 'flex',
    gap: '8px',
  },
  actionBtn: {
    padding: '6px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    backgroundColor: 'white',
    color: '#333',
    cursor: 'pointer',
    fontSize: '13px',
  },
  actionBtnSecondary: {
    padding: '6px 12px',
    border: '1px solid #2196F3',
    borderRadius: '4px',
    backgroundColor: 'white',
    color: '#2196F3',
    cursor: 'pointer',
    fontSize: '13px',
  },
  actionBtnDanger: {
    padding: '6px 12px',
    border: '1px solid #f44336',
    borderRadius: '4px',
    backgroundColor: 'white',
    color: '#f44336',
    cursor: 'pointer',
    fontSize: '13px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '8px',
    color: '#666',
  },
  roleInfo: {
    marginTop: '32px',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  roleInfoTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#333',
    marginTop: 0,
    marginBottom: '16px',
  },
  roleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
  },
  roleCard: {
    padding: '12px',
    backgroundColor: '#f9f9f9',
    borderRadius: '6px',
  },
  roleCardTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
  },
  roleCardDesc: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
  errorBanner: {
    backgroundColor: '#ffebee',
    border: '1px solid #f44336',
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '16px',
    color: '#c62828',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  successBanner: {
    backgroundColor: '#e8f5e9',
    border: '1px solid #4caf50',
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '16px',
    color: '#2e7d32',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoBanner: {
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196F3',
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '16px',
    color: '#1565c0',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'inherit',
    padding: '0 4px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '90%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a1a1a',
    margin: 0,
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    padding: '0',
  },
  formGroup: {
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'white',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  cancelBtn: {
    padding: '10px 20px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    backgroundColor: 'white',
    color: '#333',
    cursor: 'pointer',
    fontSize: '14px',
  },
  submitBtn: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#8B2635',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  },
  dangerBtn: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#f44336',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  },
  confirmText: {
    fontSize: '14px',
    color: '#333',
    marginBottom: '8px',
  },
  confirmSubtext: {
    fontSize: '13px',
    color: '#666',
  },
};

export default PortalTeam;
